import {
  generatedCas,
  PostApiUsersChangeOrganizationRoleByUserIdApiArg,
  PostApiUsersChangeOrganizationRoleByUserIdApiResponse,
} from './generated-cas';
import { apiService } from './apiService';

export const casService = generatedCas.enhanceEndpoints({
  addTagTypes: ['Users', 'User'],
  endpoints: {
    // User related endpoints
    getApiUsers: { providesTags: ['Users'] },
    patchApiUsersByUserId: { invalidatesTags: ['Users', 'User'] },
    getApiUsersByUserId: { providesTags: ['User'] },
    patchApiUsersChangePasswordByUserId: { invalidatesTags: ['User'] },
    patchApiUsersActiveByUserId: { invalidatesTags: ['User'] },
    deleteApiUsersByUserId: { invalidatesTags: ['Users'] },
    postApiUsersInvite: { invalidatesTags: ['Users', 'User'] },
    postApiUsersRegisterByInvitationToken: { invalidatesTags: ['Users', 'User'] },
    patchApiUsersProfile: { invalidatesTags: ['Users', 'User'] },
    postApiUsersAssignmentsByUserId: {
      invalidatesTags: ['Users', 'User'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(apiService.util.invalidateTags(['ProjectUsers', 'ProjectManagement', 'Projects'] as const));
        } catch (_err) {
          void _err; // prevent no-empty and avoid unhandled rejection noise
        }
      },
    },
    deleteApiUsersAssignmentsByUserId: {
      invalidatesTags: ['Users', 'User'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(apiService.util.invalidateTags(['ProjectUsers', 'ProjectManagement', 'Projects'] as const));
        } catch (_err) {
          void _err; // prevent no-empty and avoid unhandled rejection noise
        }
      },
    },

    // Global user related endpoints
    patchApiUsersAssignmentsByUserId: {
      invalidatesTags: ['Users', 'User'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(apiService.util.invalidateTags(['ProjectUsers', 'ProjectManagement', 'Projects'] as const));
        } catch (_err) {
          void _err; // prevent no-empty and avoid unhandled rejection noise
        }
      },
    },
    deleteApiUsersAssignmentsByTenantIdRemoveAndUserId: { invalidatesTags: ['Users', 'User'] },
    postApiUsersInviteGlobal: { invalidatesTags: ['Users', 'User'] },
  },
});

// Changing a user's organization role is an org-level operation: it must be scoped to the
// *edited user's* organization, not to whatever tenant is currently selected. We therefore
// skip the tenant context (which the backend prioritizes over the org context) and pin the
// organization context to the edited user's org via a per-call header.
export const casServiceExtended = casService.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    postApiUsersChangeOrganizationRoleByUserId: build.mutation<
      PostApiUsersChangeOrganizationRoleByUserIdApiResponse,
      PostApiUsersChangeOrganizationRoleByUserIdApiArg & { organizationId?: string }
    >({
      query: ({ userId, body, organizationId }) => ({
        url: `/api/users/change-organization-role/${userId}`,
        method: 'POST',
        body,
        headers: organizationId ? { 'x-organization-context-id': organizationId } : undefined,
      }),
      extraOptions: { isCas: true, skipTenantContext: true },
      invalidatesTags: ['Users', 'User'],
    }),
  }),
});

export const { usePostApiUsersChangeOrganizationRoleByUserIdMutation } = casServiceExtended;
